import type { UiMenuOptionType } from "@dpa-id-components/dpa-shared-components/src/components/UiMenu/UiMenu.vue";
import { computed, type ComputedRef, ref } from "vue";

const DEFAULT_SEARCH_MIN_OPTIONS = 10;

export function useFilterSelectorMenu(
  options: ComputedRef<UiMenuOptionType<string>[]>,
  handleFilterChange: (value: string) => void,
  searchMinOptions: number = DEFAULT_SEARCH_MIN_OPTIONS,
) {
  const menuQuery = ref("");

  const hasOptions = computed(() => options.value.length > 0);
  const hasSearch = computed(() => options.value.length >= searchMinOptions);

  const isMenuOpen = ref(false);

  const displaySelectedValues = ref<string[]>([]);

  const selectedValues = computed(() =>
    options.value.filter((o) => o.selected).map((o) => o.value),
  );

  const selectedSet = computed(() => new Set(displaySelectedValues.value));

  const sortedOptions = computed(() => {
    const set = selectedSet.value;

    const selected = options.value
      .filter((o) => set.has(o.value))
      .sort(
        (a, b) =>
          displaySelectedValues.value.indexOf(a.value) -
          displaySelectedValues.value.indexOf(b.value),
      );

    const rest = options.value.filter((o) => !set.has(o.value));

    return { selected, rest };
  });

  const handleMenuOpen = () => {
    isMenuOpen.value = true;
    displaySelectedValues.value = selectedValues.value;
  };

  const handleMenuClose = () => {
    isMenuOpen.value = false;
    displaySelectedValues.value = selectedValues.value;
    menuQuery.value = "";
  };

  const handleMenuReset = () => {
    menuQuery.value = "";

    for (const value of selectedValues.value) {
      handleFilterChange(value);
    }

    displaySelectedValues.value = [];
  };

  // Search filters the already-sorted list; order stays stable while open
  const filteredOptions = computed(() => {
    const q = menuQuery.value.trim().toLowerCase();
    if (!q)
      return [...sortedOptions.value.selected, ...sortedOptions.value.rest];

    const matches = (opt: UiMenuOptionType<string>) =>
      opt.label.toLowerCase().includes(q);

    return [
      ...sortedOptions.value.selected,
      ...sortedOptions.value.rest.filter(matches),
    ];
  });

  const displayedOptions = computed<UiMenuOptionType<string>[]>(() => {
    const set = selectedSet.value;

    const merged: UiMenuOptionType<string>[] = filteredOptions.value.map(
      (o) => ({
        ...o,
        hasDividerAbove: false,
      }),
    );

    // Divider between selected-block and rest (if both exist in the filtered list)
    if (displaySelectedValues.value.length > 0) {
      const firstUnselectedIndex = merged.findIndex((o) => !set.has(o.value));

      if (firstUnselectedIndex > 0) {
        merged[firstUnselectedIndex] = {
          ...merged[firstUnselectedIndex],
          hasDividerAbove: true,
        };
      }
    }

    return merged;
  });

  return {
    displayedOptions,
    hasOptions,
    hasSearch,
    handleMenuClose,
    handleMenuOpen,
    handleMenuReset,
    isMenuOpen,
    menuQuery,
  };
}
